/* eslint-disable sonarjs/no-duplicate-string */
import React from 'react';
import { Legend } from '../../../components/fieldset';

export const reactSelectReset = {
  control: (provided, state) => ({
    ...provided,
    borderColor: 'rgba(24, 24, 27, 0.1)',
    borderRadius: '8px',
    minHeight: '44px',
    height: '44px',
    padding: '0',
    backgroundColor: 'var(--color-brand-white-1)',
    boxShadow: state.isFocused
      ? `0 0 0 2px var(--color-brand-green-1)`
      : state.isDisabled
        ? 'none'
        : '0 1px 2px rgba(0, 0, 0, 0.1)',
    transition: 'box-shadow 0.2s ease-in-out',
    '@media (min-width: 640px)': {
      minHeight: '37px',
      height: '37px',
    },
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 12px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    '@media (min-width: 640px)': {
      height: '37px',
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.375rem',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: 'rgb(82, 82, 91)', // bg-zinc-600
      borderColor: 'rgba(255, 255, 255, 0.1)', // border-white/10
    },
  }),
  option: (provided, state) => ({
    ...provided,
    padding: '0.5rem 1rem',
    backgroundColor: state.isSelected
      ? 'var(--color-brand-green-1)'
      : state.isFocused
        ? 'var(--color-brand-green-0)'
        : 'transparent',
    color:
      state.isSelected || state.isFocused
        ? 'var(--color-brand-white-1)'
        : 'var(--color-brand-black-0)',
    '@media (prefers-color-scheme: dark)': {
      color: 'rgba(255, 255, 255, 0.9)', // text-white/90
    },
    cursor: 'pointer',
    ':active': {
      backgroundColor: 'var(--color-brand-green-1)',
      color: 'var(--color-brand-white-1)',
    },
  }),
  input: (provided) => ({
    ...provided,
    color: '#333333',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'var(--color-brand-black-0)',
    '@media (prefers-color-scheme: dark)': {
      color: 'rgba(255, 255, 255, 0.9)', // text-white/90
    },
  }),
  multiValue: (provided) => ({
    ...provided,
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)', // bg-white/15
    },
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    '@media (prefers-color-scheme: dark)': {
      color: 'rgba(255, 255, 255, 0.9)', // text-white/90
    },
  }),
};

// Shared components
export const BrandLegend = ({ children }: { children: React.ReactNode }) => (
  <Legend className="tw:text-lg tw:font-semibold tw:mb-4">
    <span className="tw:text-brand-green-0 tw:dark:text-zinc-100">{children}</span>
  </Legend>
);

type ValidationMessageProps = {
  message?: string | null;
  icon?: React.ReactNode;
  colorClass?: string;
};

export const ValidationMessage = ({ message, icon, colorClass }: ValidationMessageProps) => {
  if (!message) return null;
  return (
    <div className={`tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-sm ${colorClass}`}>
      {icon}
      {message}
    </div>
  );
};

export const LegalDocumentViewer = ({
  htmlContent,
  classNames,
}: {
  htmlContent: string;
  classNames: string;
}) => {
  const iframeRef = React.useRef(null);

  React.useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
      doc.open();
      doc.write(htmlContent);
      doc.close();
    }
  }, [htmlContent]);

  return <iframe className={classNames} ref={iframeRef} title="Marketplace Agreement" />;
};
